import React, { useMemo } from 'react';
import { EmbedData, StreamLevels } from "../..";
import useSupport from '../../hooks/Support';
import FullScreen from '../../components/FullScreen';
import Pip from '../../components/Pip';
import ChromeCast from '../../components/ChromeCast';
import AirPlay from '../../components/AirPlay';
import LogoSmartplayer from '../../components/LogoSmartplayer';
import AIButton from '../../components/AIButton';
import Configuration from '../../components/Configuration';
import Volume from '../../components/Volume';
import SwitchSummary from '../../components/SwitchSummary';
import SearchButton from '../../components/SearchButton';
import Seekbar from '../../components/Seekbar';
import PlayButton from '../../components/PlayButton';
import { AIProductsResponse } from "@src/Data/Model/AIProducts";
import ControlBarStyl from './ControlBar.styl';
import { useEffect } from "react";

export type ControlBarProps = {
  code: string;
  data: EmbedData;
  duration: number;
  levels: StreamLevels;
  videoContainer: React.RefObject<HTMLDivElement>;
  currentLevel: number;
  hls: any;
  playVideo: () => void;
  onLoadAI: () => void;
  summaryState: {
    openSummary: boolean;
    setOpenSummary: React.Dispatch<React.SetStateAction<boolean>>
  };
  summaryTextState: {
    openSummaryText: boolean;
    setOpenSummaryText: React.Dispatch<React.SetStateAction<boolean>>
  };
  mindMapState: {
    openMindMap: boolean;
    setOpenMindMap: React.Dispatch<React.SetStateAction<boolean>>
  };
  quizState: {
    openQuiz: boolean;
    setOpenQuiz: React.Dispatch<React.SetStateAction<boolean>>
  };
  searchState: {
    openSearch: boolean;
    setOpenSearch: React.Dispatch<React.SetStateAction<boolean>>
  };
  aiProductsState: {
    aiProducts: AIProductsResponse | null;
    setAIProducts: React.Dispatch<React.SetStateAction<AIProductsResponse | null>>
  };
};

function detectDevice(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Verifica se é iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return true;
  }

  // Verifica se é Android
  if (/android/i.test(userAgent)) {
    return true;
  }

  // Verifica se é mobile (geral)
  if (/Mobi|Android/i.test(userAgent)) {
    return true;
  }

  // Se não for nenhum dos anteriores, assume que é desktop
  return false;
}

const ControlBar = ({
  code,
  data,
  duration,
  levels,
  videoContainer,
  currentLevel,
  hls,
  playVideo,
  onLoadAI,
  summaryState,
  summaryTextState,
  mindMapState,
  quizState,
  searchState,
  aiProductsState
}: ControlBarProps) => {
  const supported = useSupport();

  const [isMagicButton, setIsMagicButton] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [lastActivity, setLastActivity] = React.useState(Date.now());

  const { openSummary, setOpenSummary } = summaryState;
  const { openSummaryText, setOpenSummaryText } = summaryTextState;
  const { openMindMap, setOpenMindMap } = mindMapState;
  const { openQuiz, setOpenQuiz } = quizState;
  const { openSearch, setOpenSearch } = searchState;
  const { aiProducts, setAIProducts } = aiProductsState;

  const onBackdropClick = (event: any) => {
    const footer = event?.target?.parentElement;

    if (!footer) {
      return;
    }

    const configurationBtn = footer.querySelector('.smartplayer__configuration');

    configurationBtn?.classList.remove('smartplayer__configuration--opened');
    configurationBtn?.classList.remove('smartplayer__quality--opened');
    configurationBtn?.classList.remove('smartplayer__velocity--opened');
    configurationBtn?.classList.remove('smartplayer__subtitle--opened');

    const aiButton = footer.querySelector('.smartplayer__ai');

    aiButton?.classList.remove('smartplayer__ai--opened');

    // footer.classList.remove('smartplayer__footer--dropdown');

    const volumeButton = footer.querySelector('.smartplayer__volume') as HTMLButtonElement;
    volumeButton?.classList.remove('smartplayer__volume--opened');

    setOpenMindMap(false);
    setOpenSummary(false);
    setOpenSummaryText(false);
    setOpenQuiz(false);
    setOpenSearch(false);
  };

  // const onMouseMove = () => {

  // };

  // const onMouseEnter = () => {

  // };

  // const onMouseOver = () => {

  // };

  // useEffect(() => {
  //   const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

  //   rootElement.addEventListener('mousemove', onMouseMove, false);
  //   rootElement.addEventListener('mouseenter', onMouseEnter, false);
  //   rootElement.addEventListener('mouseover', onMouseOver, false);

  //   return () => {
  //     rootElement.removeEventListener('mousemove', onMouseMove, false);
  //     rootElement.removeEventListener('mouseenter', onMouseEnter, false);
  //     rootElement.removeEventListener('mouseover', onMouseOver, false);
  //   }
  // }, [code]);

  const setInactive = React.useCallback(() => {
    setLastActivity(Date.now() - 5000);
    setIsVisible(false);
  }, []);

  const handleActivity = React.useCallback(() => {
    if (openSummary || openSummaryText || openMindMap || openQuiz || openSearch) {
      return;
    }

    setLastActivity(Date.now());
    setIsVisible(true);
  }, [openSummary, openSummaryText, openMindMap, openQuiz, openSearch]);

  const changeCheckbox = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const checkbox = event.currentTarget?.previousElementSibling as HTMLInputElement;

    checkbox.checked = !checkbox.checked;
  }, []);

  useEffect(() => {
    setIsMagicButton(detectDevice());
  }, [])

  useEffect(() => {
    if (!data?.config?.controls) {
      return;
    }

    const inactivityTime = isMagicButton ? 5000 : 3000;

    const checkInactivity = setInterval(() => {
      if (Date.now() - lastActivity > inactivityTime) {
        setIsVisible(false);
      }
    }, 1000);

    const events = [
      'mousemove',
      'mouseenter',
      'mouseover',
      'touchstart',
      'touchmove'
    ];

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(checkInactivity);
    };
  }, [isMagicButton, lastActivity, handleActivity, data, data?.config]);

  useEffect(() => {
    if (isVisible || !data?.config?.controls) {
      return;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

    const volumeButton = rootElement?.querySelector('.smartplayer__volume') as HTMLButtonElement;

    if (volumeButton) {
      volumeButton.classList.remove('smartplayer__volume--opened');
    }

    const aiButton = rootElement?.querySelector('.smartplayer__ai') as HTMLButtonElement;

    if (aiButton) {
      aiButton.classList.remove('smartplayer__ai--opened');
    }

    const menuElement = rootElement?.querySelector('.smartplayer__configuration') as HTMLDivElement;

    const hasOpened = menuElement?.classList.contains('smartplayer__configuration--opened')
                   || menuElement?.classList.contains('smartplayer__quality--opened')
                   || menuElement?.classList.contains('smartplayer__velocity--opened')
                   || menuElement?.classList.contains('smartplayer__subtitle--opened');

    const quality = rootElement?.querySelector('#quality') as HTMLInputElement;

    if (quality) {
      quality.checked = false;
    }

    const velocity = rootElement?.querySelector('#velocity') as HTMLInputElement;

    if (velocity) {
      velocity.checked = false;
    }

    const subtitle = rootElement?.querySelector('#subtitle') as HTMLInputElement;

    if (subtitle) {
      subtitle.checked = false;
    }

    if (hasOpened) {
      menuElement?.classList.remove('smartplayer__configuration--opened');
      menuElement?.classList.remove('smartplayer__quality--opened');
      menuElement?.classList.remove('smartplayer__velocity--opened');
      menuElement?.classList.remove('smartplayer__subtitle--opened');
    }

    const elementCbx = rootElement?.querySelector('.smartplayer__magicButtonCbx') as HTMLInputElement;

    if (isMagicButton && elementCbx) {
      elementCbx.checked = false;
    }
  }, [isVisible, data, data?.config]);

  const buttons = useMemo(() => {
    return (
      <>
        <SearchButton
          enabled={!!(data?.ai && data?.ai?.indexOf('SMART_SEARCH') > -1)}
          aiProducts={data?.ai || ''}
          code={data?.code || ''}
          onClick={() => {
            const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

            // if (!openSearch) {
            // rootElement.querySelector('.smartplayer__footer')?.classList.add('smartplayer__footer--dropdown');
            // } else {
            // rootElement.querySelector('.smartplayer__footer')?.classList.remove('smartplayer__footer--dropdown');
            // }

            setInactive();
            setOpenSearch(!openSearch);
            setOpenSummary(false);
            setOpenSummaryText(false);
            setOpenMindMap(false);
            setOpenQuiz(false);
          }}
        />
        {/* <div className="smartplayer__subtitle">
          <button className="smartplayer__bar-button" type="button" onClick={(event) => {
            event.currentTarget?.parentElement?.classList.toggle('smartplayer__subtitle--opened');
          }} disabled={!supported.subtitles}>
            <div className="icon icon--subtitle">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="13" viewBox="0 0 16 13" fill="none">
                <rect y="0.279785" width="16" height="12.44" rx="2" fill="currentColor"></rect>
                <path d="M2.64526 8.6816V4.31796H3.56785V7.92094H5.43859V8.6816H2.64526Z" fill="black"></path>
                <path d="M5.92956 8.6816V4.31796H8.8699V5.07861H6.85215V6.11839H8.71862V6.87904H6.85215V7.92094H8.87842V8.6816H5.92956Z" fill="black"></path>
                <path d="M12.3769 5.72847C12.347 5.62478 12.3051 5.53316 12.2511 5.45361C12.1972 5.37265 12.1311 5.30447 12.053 5.24907C11.9763 5.19225 11.8882 5.14893 11.7888 5.1191C11.6908 5.08927 11.5821 5.07435 11.4628 5.07435C11.2398 5.07435 11.0438 5.12975 10.8747 5.24055C10.7071 5.35134 10.5764 5.51256 10.4827 5.72421C10.3889 5.93444 10.3421 6.19154 10.3421 6.49552C10.3421 6.79949 10.3882 7.05802 10.4805 7.27108C10.5729 7.48415 10.7036 7.6468 10.8726 7.75901C11.0416 7.86981 11.2412 7.9252 11.4713 7.9252C11.6801 7.9252 11.8584 7.88827 12.0061 7.81441C12.1553 7.73912 12.2689 7.6333 12.347 7.49694C12.4266 7.36057 12.4663 7.19935 12.4663 7.01327L12.6538 7.04097H11.5288V6.34637H13.3548V6.89608C13.3548 7.27961 13.2739 7.60915 13.1119 7.88472C12.95 8.15887 12.727 8.37052 12.4429 8.51966C12.1588 8.66739 11.8335 8.74126 11.4671 8.74126C11.058 8.74126 10.6986 8.65106 10.3889 8.47066C10.0793 8.28884 9.83779 8.03103 9.6645 7.69722C9.49262 7.36199 9.40668 6.96427 9.40668 6.50404C9.40668 6.15035 9.45782 5.83501 9.56009 5.55802C9.66379 5.27961 9.80867 5.04381 9.99475 4.85063C10.1808 4.65745 10.3975 4.51043 10.6446 4.40958C10.8918 4.30873 11.1595 4.2583 11.4479 4.2583C11.695 4.2583 11.9252 4.29452 12.1382 4.36697C12.3513 4.43799 12.5402 4.53884 12.705 4.66952C12.8712 4.8002 13.0068 4.95574 13.1119 5.13614C13.2171 5.31512 13.2845 5.51256 13.3144 5.72847H12.3769Z" fill="black"></path>
              </svg>
            </div>
          </button>
        </div> */}
        <AIButton
          enabled={!!data?.ai}
          aiProducts={data?.ai || ''}
          code={data?.code || ''}
          hasAIOpened={openSummaryText || openSummary || openMindMap || openQuiz || openSearch}
          onLoadAI={onLoadAI}
          openEbook={() => {
            setInactive();
            setOpenSummary(true);
            setOpenSummaryText(false);
            setOpenMindMap(false);
            setOpenQuiz(false);
            setOpenSearch(false);
          }}
          openMindMap={() => {
            setInactive();
            setOpenSummary(false);
            setOpenSummaryText(false);
            setOpenMindMap(true);
            setOpenQuiz(false);
            setOpenSearch(false);
          }}
          openQuiz={() => {
            setInactive();
            setOpenSummary(false);
            setOpenSummaryText(false);
            setOpenMindMap(false);
            setOpenQuiz(true);
            setOpenSearch(false);
          }}
          openSummaryText={() => {
            setInactive();
            setOpenSummary(false);
            setOpenSummaryText(true);
            setOpenMindMap(false);
            setOpenQuiz(false);
            setOpenSearch(false);
          }}
        />
        <Configuration
          enabled={!!data?.config?.showConfigurations}
          hls={hls}
          code={data?.code || ''}
          levels={levels || []}
          currentLevel={currentLevel || 0}
          subtitles={data?.subtitles || []}
          hasAIOpened={openSummary || openMindMap || openQuiz || openSearch}
        />
        <AirPlay
          authorization={data?.authorization || ''}
          enabled={!!data?.config?.airplay}
          hls={hls}
          video={data?.video || ''}
          // subtitles={data?.subtitles || []}
          code={data?.code || ''}
          videoName={data?.name || ''}
          videoPoster={data?.poster || ''}
          supported={supported}
        />
        <ChromeCast
          authorization={data?.authorization || ''}
          enabled={!!data?.config?.chromecast}
          video={data?.video || ''}
          // subtitles={data?.subtitles || []}
          code={data?.code || ''}
          videoName={data?.name || ''}
          videoPoster={data?.poster || ''}
          supported={supported}
        />
        <Pip
          enabled={!!data?.config?.pip}
          hls={hls}
          code={data?.code || ''}
          supported={supported}
        />
        <FullScreen
          enabled={!!data?.config?.fullScreen}
          code={data?.code || ''}
          supported={supported}
        />
      </>
    );
  }, [data, data?.config]);

  return data?.config?.controls ? (
    <ControlBarStyl>
      <div className={`smartplayer__footer ${isVisible ? 'smartplayer__footer--visible' : 'smartplayer__footer--hidden'}`}>
        <div className="smartplayer__footerBackDrop" onClick={onBackdropClick}></div>
        <PlayButton
          code={data?.code || ''}
          onClick={() => playVideo()}
        />
        <SwitchSummary
          enabled={!!(data?.ai && data?.ai?.indexOf('SUMMARY') > -1)}
          hls={hls}
          code={data?.code || ''}
          primaryColor={data?.primaryColor || ''}
          textColor={data?.textColor || ''}
          videoUrl={data?.video || ''}
          summaryVideoUrl={data?.summaryVideo || ''}
        />
        <div className={`smartplayer__controlbar smartplayer__controlbar--${(!!data?.config?.emptyControlBar ? 'empty' : 'normal')}`}>
          <Volume
            videoContainer={videoContainer}
            enabled={!!data?.config?.volume}
            hls={hls}
            code={data?.code || ''}
          />
          <Seekbar
            enabled={!!data?.config?.progressBar}
            hls={hls}
            code={data?.code || ''}
            duration={duration || 0}
            videoContainer={videoContainer}
          />
          {
            isMagicButton ? null : (
              <div className="smartplayer__controlbarActions">
                {buttons}
              </div>
            )
          }
        </div>
        <LogoSmartplayer
          enabled={!!data?.config?.logo}
          code={data?.code || ''}
        />
      </div>
      {
        isMagicButton && !data?.config?.emptyControlBar ? (
          <div className="smartplayer__magicButtonContainer">
            <input type="checkbox" className="smartplayer__magicButtonCbx" />
            <button type="button" className="smartplayer__magicButton" onClick={
              (event: React.MouseEvent<HTMLButtonElement>) => {
                changeCheckbox(event);
              }
            }>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 12.1366C6 11.71 6.16389 11.3009 6.4556 10.9992C6.74732 10.6975 7.14298 10.5281 7.55553 10.5281C7.96808 10.5281 8.36374 10.6975 8.65545 10.9992C8.94717 11.3009 9.11106 11.71 9.11106 12.1366C9.11106 12.5633 8.94717 12.9724 8.65545 13.2741C8.36374 13.5757 7.96808 13.7452 7.55553 13.7452C7.14298 13.7452 6.74732 13.5757 6.4556 13.2741C6.16389 12.9724 6 12.5633 6 12.1366ZM10.4444 12.1366C10.4444 11.71 10.6083 11.3009 10.9 10.9992C11.1917 10.6975 11.5873 10.5281 11.9999 10.5281C12.4124 10.5281 12.8081 10.6975 13.0998 10.9992C13.3915 11.3009 13.5554 11.71 13.5554 12.1366C13.5554 12.5633 13.3915 12.9724 13.0998 13.2741C12.8081 13.5757 12.4124 13.7452 11.9999 13.7452C11.5873 13.7452 11.1917 13.5757 10.9 13.2741C10.6083 12.9724 10.4444 12.5633 10.4444 12.1366ZM16.4443 10.5281C16.8568 10.5281 17.2525 10.6975 17.5442 10.9992C17.8359 11.3009 17.9998 11.71 17.9998 12.1366C17.9998 12.5633 17.8359 12.9724 17.5442 13.2741C17.2525 13.5757 16.8568 13.7452 16.4443 13.7452C16.0317 13.7452 15.6361 13.5757 15.3443 13.2741C15.0526 12.9724 14.8887 12.5633 14.8887 12.1366C14.8887 11.71 15.0526 11.3009 15.3443 10.9992C15.6361 10.6975 16.0317 10.5281 16.4443 10.5281Z" fill="white"/>
              </svg>
            </button>
            <div className="smartplayer__magicButtons">
              {buttons}
            </div>
          </div>
        ) : null
      }
    </ControlBarStyl>
  ) : null;
};

export default ControlBar;
